import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';
import { waitForCompanyID } from '@/js/external-js/variableModules/utils/waitForCompanyID.util';
import { getAuthUser } from '@/lib/authState';

const LEGAL_OBLIGATIONS_API = '/v1/legal-obligations';

// Interface for the processed LegalObligation
export interface LegalObligation {
  id: string;
  addedBy: string;
  dateAdded: Date;
  dateUpdated: Date;
  owner: string;
  requirement: string;
  updateMethod: string;
  updatedBy: string;
}

// Interface for creating/updating legal obligations
export interface LegalObligationInput {
  owner?: string;
  requirement?: string;
  updateMethod?: string;
}

// Helper to get headers
async function getHeaders() {
  const companyID = await waitForCompanyID();
  const user = getAuthUser();
  return {
    'X-Tenancy-Id': companyID,
    'Company-ID': companyID,
    'X-User-Id': user?.email || user?.uid || 'unknown',
  };
}

// Helper to parse dates from API response
function parseObligation(obligation: any): LegalObligation {
  return {
    id: obligation.id || '',
    addedBy: obligation.addedBy || '',
    dateAdded: obligation.dateAdded ? new Date(obligation.dateAdded) : new Date(),
    dateUpdated: obligation.dateUpdated ? new Date(obligation.dateUpdated) : new Date(),
    owner: obligation.owner || '',
    requirement: obligation.requirement || '',
    updateMethod: obligation.updateMethod || '',
    updatedBy: obligation.updatedBy || '',
  };
}

/**
 * Fetches all legal obligations via Thalassa API
 */
export async function getAllLegalObligations(): Promise<LegalObligation[]> {
  try {
    const headers = await getHeaders();
    const response = await deiterateAPI.GET(LEGAL_OBLIGATIONS_API, {
      mode: 'cors',
      backend: 'thalassa',
      headers,
    });

    if (response.data && Array.isArray(response.data)) {
      return response.data.map(parseObligation);
    }

    console.warn('No legal obligations data found. Response:', response);
    return [];
  } catch (error) {
    console.error('Error fetching legal obligations:', error);
    throw new Error('Failed to fetch legal obligations');
  }
}

/**
 * Fetches a single legal obligation by ID via Thalassa API
 */
export async function getLegalObligation(id: string): Promise<LegalObligation | null> {
  try {
    const headers = await getHeaders();
    const response = await deiterateAPI.GET(`${LEGAL_OBLIGATIONS_API}/${id}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers,
    });

    if (response.data) {
      return parseObligation(response.data);
    }

    return null;
  } catch (error) {
    console.error(`Error fetching legal obligation ${id}:`, error);
    throw new Error(`Failed to fetch legal obligation ${id}`);
  }
}

/**
 * Creates a new legal obligation via Thalassa API
 */
export async function createLegalObligation(
  data: LegalObligationInput
): Promise<LegalObligation> {
  try {
    const headers = await getHeaders();
    const response = await deiterateAPI.POST(LEGAL_OBLIGATIONS_API, {
      mode: 'cors',
      backend: 'thalassa',
      headers,
      body: JSON.stringify(data),
    });

    if (response.statusCode === 200 && response.legalObligation) {
      console.debug('[legalObligationsService:createLegalObligation]', data, response);
      return parseObligation(response.legalObligation);
    }

    throw new Error(response.error || 'Failed to create legal obligation');
  } catch (error) {
    console.error('Error creating legal obligation:', error);
    throw error instanceof Error ? error : new Error('Failed to create legal obligation');
  }
}

/**
 * Updates an existing legal obligation via Thalassa API
 */
export async function updateLegalObligation(
  id: string,
  updates: LegalObligationInput
): Promise<LegalObligation> {
  try {
    const headers = await getHeaders();
    const response = await deiterateAPI.PUT(`${LEGAL_OBLIGATIONS_API}/${id}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers,
      body: JSON.stringify(updates),
    });

    if (response.statusCode === 200 && response.legalObligation) {
      return parseObligation(response.legalObligation);
    }

    throw new Error(response.error || 'Failed to update legal obligation');
  } catch (error) {
    console.error(`Error updating legal obligation ${id}:`, error);
    throw error instanceof Error ? error : new Error(`Failed to update legal obligation ${id}`);
  }
}

/**
 * Deletes a legal obligation via Thalassa API
 */
export async function deleteLegalObligation(id: string): Promise<boolean> {
  try {
    const headers = await getHeaders();
    const response = await deiterateAPI.DELETE(`${LEGAL_OBLIGATIONS_API}/${id}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers,
    });

    if (response) {
      console.debug('[legalObligationsService:deleteLegalObligation]', `Removed ID: ${id}`);
      return true;
    }

    return false;
  } catch (error) {
    console.error(`Error deleting legal obligation ${id}:`, error);
    return false;
  }
}

/**
 * Polling-based listener for legal obligations changes
 * Note: Real-time listeners require Firestore SDK. This polls the API instead.
 */
export function onLegalObligationsChange(
  callback: (obligations: LegalObligation[]) => void,
  pollInterval: number = 30000
): () => void {
  let isActive = true;
  let timeoutId: NodeJS.Timeout | null = null;

  const poll = async () => {
    if (!isActive) return;

    try {
      const obligations = await getAllLegalObligations();
      if (isActive) {
        callback(obligations);
      }
    } catch (error) {
      console.error('Error polling legal obligations:', error);
    }

    if (isActive) {
      timeoutId = setTimeout(poll, pollInterval);
    }
  };

  // Initial fetch
  poll();

  // Return unsubscribe function
  return () => {
    isActive = false;
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  };
}


export default {
  getAllLegalObligations,
  getLegalObligation,
  createLegalObligation,
  updateLegalObligation,
  deleteLegalObligation,
};
